import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import { Loader2, BookOpenCheck, Download, RefreshCw, Quote, UserRound, Gauge, Minus, Check } from "lucide-react";

const REPORT_META = {
  em_dash_audit: { icon: Minus, blurb: "Count per chapter and the 30 densest passages, each with punctuation-only alternatives." },
  quotation_audit: { icon: Quote, blurb: "Every quotation from page 84 on, flagged for your review. No attribution is ever invented." },
  narrator_voice_audit: { icon: UserRound, blurb: "Passages near page 105 (and elsewhere) that use gendered language. Nothing changed." },
  readability_top15: { icon: Gauge, blurb: "The 15 densest passages by reading-grade level — the starting set for Plain Words." },
};

export default function AdminBookReview() {
  const [token] = useState(() => getAdminToken());
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [data, setData] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [qDraft, setQDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, q] = await Promise.all([
        axios.get(`${API}/admin/audits`, auth),
        axios.get(`${API}/admin/quotations`, auth).catch(() => ({ data: { quotations: [] } })),
      ]);
      setData(a.data);
      setQuotations(q.data.quotations || []);
    } catch (e) {
      if (e.response?.status === 401) adminLogout();
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/audits/run`, {}, auth);
      setData(data);
      const q = await axios.get(`${API}/admin/quotations`, auth).catch(() => ({ data: { quotations: [] } }));
      setQuotations(q.data.quotations || []);
    } catch (e) {
      if (e.response?.status === 401) adminLogout();
    } finally { setRunning(false); }
  };

  const markQuote = async (id, patch) => {
    try { await axios.put(`${API}/admin/quotations/${id}`, patch, auth); await load(); }
    catch (e) { if (e.response?.status === 401) adminLogout(); }
  };

  const download = async (filename) => {
    try {
      const res = await axios.get(`${API}/admin/audits/download/${filename}`, { ...auth, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `braced-${filename}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* no-op */ }
  };

  const s = data?.summary;

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-book-review">
      <div className="mx-auto max-w-5xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="inline-flex items-center gap-2 font-display text-3xl text-ink">
              <BookOpenCheck className="h-6 w-6 text-gold-deep" /> Book Review
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-ink-soft">
              Read-only audits of the book. These reports never change a single word of the prose — they
              only help you decide what to edit. Review them, then tell me which changes to apply.
            </p>
          </div>
          <button onClick={run} disabled={running} data-testid="run-audits-btn"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-display text-sm text-ivory-50 transition hover:bg-ink/90 disabled:opacity-50">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {data?.generated ? "Re-run audits" : "Run audits"}
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-5 py-6 sm:px-8">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>
        ) : !data?.generated ? (
          <div className="rounded-2xl bg-ivory-50 p-10 text-center ring-1 ring-ivory-400/50" data-testid="audits-empty">
            <BookOpenCheck className="mx-auto h-7 w-7 text-ink-muted" />
            <p className="mt-3 font-display text-lg text-ink">No audits yet</p>
            <p className="mt-1 text-sm text-ink-muted">Run the audits to generate the em dash, quotation, narrator-voice and readability reports.</p>
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="audit-stats">
              {[["Pages scanned", data.page_count], ["Em dashes (book)", s.em_dash_total], ["Em dashes (site)", s.em_dash_site_copy_total], ["Quotations (pg 84+)", s.quotations_found], ["Reader-gender flags", s.narrator_flags]].map(([k, v]) => (
                <div key={k} className="rounded-2xl bg-ivory-50 p-4 text-center ring-1 ring-ivory-400/50">
                  <p className="font-display text-2xl text-ink">{v}</p>
                  <p className="text-xs text-ink-muted">{k}</p>
                </div>
              ))}
            </div>

            {/* Downloadable reports */}
            <h2 className="mt-8 font-display text-xl text-ink">Reports</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="audit-reports">
              {(data.files || []).map((f) => {
                const meta = REPORT_META[f.name] || {};
                const Icon = meta.icon || Download;
                return (
                  <div key={f.name} className="rounded-2xl bg-ivory-50 p-5 ring-1 ring-ivory-400/50">
                    <p className="inline-flex items-center gap-2 font-display text-lg text-ink">
                      <Icon className="h-5 w-5 text-gold-deep" /> {f.title}
                    </p>
                    <p className="mt-1 text-sm text-ink-soft">{meta.blurb}</p>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => download(f.md)} data-testid={`download-${f.name}-md`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-ivory-400/70 px-3.5 py-2 text-xs font-display text-ink transition hover:border-gold/50 hover:text-gold-deep">
                        <Download className="h-3.5 w-3.5" /> Markdown
                      </button>
                      <button onClick={() => download(f.json)} data-testid={`download-${f.name}-json`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-ivory-400/70 px-3.5 py-2 text-xs font-display text-ink transition hover:border-gold/50 hover:text-gold-deep">
                        <Download className="h-3.5 w-3.5" /> JSON
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Em dash per chapter */}
            <h2 className="mt-8 font-display text-xl text-ink">Em dashes per chapter</h2>
            <div className="mt-3 overflow-hidden rounded-2xl ring-1 ring-ivory-400/50" data-testid="emdash-table">
              <table className="w-full text-left text-sm">
                <thead className="bg-ivory-50 text-[11px] uppercase tracking-wide text-ink-muted">
                  <tr><th className="px-4 py-3">Chapter</th><th className="px-4 py-3 text-right">Em dashes</th></tr>
                </thead>
                <tbody>
                  {(s.em_dash_per_chapter || []).map((c) => (
                    <tr key={c.chapter} className="border-t border-ivory-400/40 bg-white/60">
                      <td className="px-4 py-2.5 text-ink">{c.chapter}</td>
                      <td className="px-4 py-2.5 text-right text-ink">{c.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Readability top 15 */}
            <h2 className="mt-8 font-display text-xl text-ink">Readability — 15 densest passages</h2>
            <p className="mt-1 text-xs text-ink-muted">Flesch-Kincaid grade (higher = denser). These pages seed the Plain Words layer.</p>
            <div className="mt-3 overflow-hidden rounded-2xl ring-1 ring-ivory-400/50" data-testid="readability-table">
              <table className="w-full text-left text-sm">
                <thead className="bg-ivory-50 text-[11px] uppercase tracking-wide text-ink-muted">
                  <tr>
                    <th className="px-4 py-3">#</th><th className="px-4 py-3">Page</th><th className="px-4 py-3">Chapter</th>
                    <th className="px-4 py-3 text-right">FK grade</th><th className="px-4 py-3 text-right">Ease</th><th className="px-4 py-3 text-right">Avg sent.</th>
                  </tr>
                </thead>
                <tbody>
                  {(s.readability_top || []).map((r, i) => (
                    <tr key={`${r.page}-${i}`} className="border-t border-ivory-400/40 bg-white/60">
                      <td className="px-4 py-2.5 text-ink-muted">{i + 1}</td>
                      <td className="px-4 py-2.5 text-ink">{r.page}</td>
                      <td className="px-4 py-2.5 text-ink-soft">{r.chapter}</td>
                      <td className="px-4 py-2.5 text-right text-ink">{r.flesch_kincaid_grade}</td>
                      <td className="px-4 py-2.5 text-right text-ink-soft">{r.flesch_reading_ease}</td>
                      <td className="px-4 py-2.5 text-right text-ink-soft">{r.avg_sentence_length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-6 text-xs text-ink-muted">
              Generated {data.generated_at ? new Date(data.generated_at).toLocaleString() : ""}. The book PDF is
              unchanged — these are reports only.
            </p>

            {/* Quotation marking */}
            <h2 className="mt-9 font-display text-xl text-ink">Quotations — mark each one</h2>
            <p className="mt-1 text-xs text-ink-muted">
              Tag each quote so I know how to treat it. Originals can become a distinct “Reflection” element (no quote
              marks) and outside sources get the attribution you provide — applied when you send the revised PDF.
            </p>
            <div className="mt-3 space-y-3" data-testid="quotation-marker">
              {quotations.length === 0 ? (
                <p className="text-sm text-ink-muted">No quotations yet — run the audits above.</p>
              ) : quotations.map((q) => {
                const d = qDraft[q.id] || {};
                const mark = d.mark ?? q.mark;
                const attribution = d.attribution ?? q.attribution ?? "";
                return (
                  <div key={q.id} className="rounded-2xl bg-ivory-50 p-5 ring-1 ring-ivory-400/50" data-testid={`quote-${q.id}`}>
                    <p className="text-xs text-ink-muted">Page {q.page} · {q.chapter} · auto-flag: {q.auto_flag}</p>
                    <p className="mt-1.5 font-display text-lg leading-snug text-ink">“{q.quote}”</p>
                    <p className="mt-1 text-xs italic text-ink-muted">…{q.context}…</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {[["original", "Casey's original"], ["outside", "Outside source"], ["unsure", "Unsure"]].map(([val, lbl]) => (
                        <button key={val} onClick={() => markQuote(q.id, { mark: val, attribution })} data-testid={`quote-${q.id}-${val}`}
                          className={`rounded-full border px-3.5 py-1.5 text-xs transition ${mark === val ? "border-gold bg-gold/15 text-gold-deep" : "border-ivory-400/70 text-ink-soft hover:border-gold/40"}`}>
                          {mark === val && <Check className="mr-1 inline h-3 w-3" />}{lbl}
                        </button>
                      ))}
                    </div>
                    {mark === "outside" && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <input value={attribution} onChange={(e) => setQDraft((s) => ({ ...s, [q.id]: { ...d, mark, attribution: e.target.value } }))}
                          placeholder="Attribution line (e.g. — Author, Title)" data-testid={`quote-${q.id}-attr`}
                          className="flex-1 rounded-lg border border-ivory-400/70 bg-[#FCFBF7] px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                        <button onClick={() => markQuote(q.id, { mark, attribution })} data-testid={`quote-${q.id}-save`}
                          className="rounded-full border border-ivory-400/70 px-3.5 py-2 text-xs font-display text-ink hover:border-gold/50">Save</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
